// src/components/FeeManagement/FeeTermList.js

import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, parse } from 'date-fns';
import {
    Box, Typography, Button, CircularProgress, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Select, MenuItem, FormControl,
    InputLabel, Chip, Tooltip, IconButton, Snackbar, Alert, useTheme, useMediaQuery,
    Card, CardContent, Divider, Container, Grid
} from '@mui/material';
import { ThemeProvider, styled, alpha } from '@mui/material/styles';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import FeeTermForm from './FeeTermForm';
import axiosInstance from '../../../api/axiosConfig';
import SimpleModal from '../../mobile/SimpleModal';

// --- API and Helper Functions ---
const API_URL = '/api/mobile/fee-terms';

const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = dateString.includes('T') ? parseISO(dateString) : parse(dateString, 'yyyy-MM-dd', new Date());
        if (isNaN(date.getTime())) throw new Error("Invalid date");
        return format(date, 'MMM d, yyyy');
    } catch (e) { console.warn("Date format error:", dateString, e); return 'Invalid Date'; }
};

const formatAmount = (percentage, fixed) => {
    if (percentage != null && !isNaN(percentage)) return `${Number(percentage).toFixed(2)}%`;
    if (fixed != null && !isNaN(fixed)) return `₹ ${Number(fixed).toFixed(2)}`;
    return 'N/A';
};

// --- Modern Styled Components ---
const PageBackground = styled(Box)(({ theme }) => ({
    backgroundColor: 'transparent',
    minHeight: '100%',
    padding: theme.spacing(0.4),[theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.2),
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 26,
    boxShadow: '0 18px 38px rgba(15, 23, 42, 0.05)',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
    background: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.98)} 0%, ${alpha('#ECFDF5', 0.45)} 100%)`,
}));

const HeroStrip = styled(Box)(({ theme }) => ({
    borderRadius: 6,
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${alpha('#ECFDF5', 0.92)} 0%, ${alpha('#FFFFFF', 1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.success.main, 0.14)}`,
    marginBottom: theme.spacing(2.2),
}));

const MetricTile = styled(Box)(({ theme }) => ({
    borderRadius: 6,
    padding: theme.spacing(1.6),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
}));

const StyledTable = styled(Table)(({ theme }) => ({
    minWidth: 700,
    '& thead th': {
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : '#f8fafc',
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        padding: '16px',
        borderBottom: `2px solid ${theme.palette.divider}`,
    },
    '& tbody tr': {
        transition: 'background-color 0.2s ease',
    },
    '& tbody tr:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },[theme.breakpoints.down('sm')]: {
        minWidth: '100%',
    }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.875rem',
    padding: '14px 16px',
    color: theme.palette.text.primary,
}));

const AnimatedTableRow = styled(TableRow)(({ theme }) => ({
    transition: 'background-color 0.15s ease',
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
    
    [theme.breakpoints.down('sm')]: {
        display: 'block',
        padding: theme.spacing(2),
        margin: theme.spacing(2, 0),
        backgroundColor: theme.palette.background.paper,
        borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': { backgroundColor: theme.palette.background.paper, transform: 'none' },
        '& .MuiTableCell-root': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px dashed ${theme.palette.divider}`,
            padding: theme.spacing(1.5, 0),
            textAlign: 'right',
            '&:last-child': { 
                borderBottom: 'none', 
                justifyContent: 'flex-end', 
                paddingTop: theme.spacing(2),
                paddingBottom: 0
            },
        },
    },
}));

const MobileLabel = styled(Typography)(({ theme }) => ({
    display: 'none',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginRight: theme.spacing(2),
    minWidth: '100px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
        display: 'inline-block',
    },
}));

const NoResultsContainer = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(8, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 6,
    border: `1px dashed ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
}));

const ActionIconButton = styled(IconButton)(({ theme }) => ({
    padding: theme.spacing(0.8),
    transition: 'background-color 0.2s ease, color 0.2s ease',
    color: theme.palette.text.secondary,
    borderRadius: '8px',
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
    '&.edit:hover': { color: theme.palette.primary.main },
    '&.delete:hover': { color: theme.palette.error.main, backgroundColor: alpha(theme.palette.error.main, 0.1) },
}));

const CenteredBox = styled(Box)({
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '300px',
});

// --- Main Component ---
function FeeTermList() {
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const[listError, setListError] = useState(null);
    const [schoolId, setSchoolId] = useState(localStorage.getItem('schoolId') || null);
    const [activeFilter, setActiveFilter] = useState('active');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const[modalMode, setModalMode] = useState('create');
    const [editingTerm, setEditingTerm] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const[modalError, setModalError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    
    const uiTheme = useTheme();
    const isMobile = useMediaQuery(uiTheme.breakpoints.down('sm'));
    const activeTermsCount = terms.filter((term) => term.isActive).length;

    const handleSnackbarClose = (event, reason) => { if (reason === 'clickaway') return; setSnackbarOpen(false); };
    const showSnackbar = (message, severity = 'success') => { setSnackbarMessage(message); setSnackbarSeverity(severity); setSnackbarOpen(true); };

    // --- Fetch Logic ---
    const fetchTerms = useCallback(async (filter = activeFilter, isRetry = false) => {
        if (!schoolId) { setListError("School ID not found. Please select a school."); setLoading(false); setTerms([]); return; }
        if (!isRetry) setLoading(true); setListError(null);
        try {
            const params = { schoolId }; 
            if (filter === 'active') params.isActive = true; 
            else if (filter === 'inactive') params.isActive = false;
            
            const response = await axiosInstance.get(API_URL, { params });
            const sorted = (response.data ||[]).sort((a, b) => {
                const ordA = a.displayOrder ?? Infinity, ordB = b.displayOrder ?? Infinity; 
                if (ordA !== ordB) return ordA - ordB;
                const dtA = a.dueDate ? parse(a.dueDate, 'yyyy-MM-dd', new Date()).getTime() : Infinity;
                const dtB = b.dueDate ? parse(b.dueDate, 'yyyy-MM-dd', new Date()).getTime() : Infinity; 
                if (dtA !== dtB) return dtA - dtB;
                return (a.termName || '').localeCompare(b.termName || '');
            }); 
            setTerms(sorted);
        } catch (err) { 
            console.error("Fetch Error:", err); 
            const msg = err.response?.data?.message || 'Failed to load fee terms.'; 
            setListError(msg); 
            showSnackbar(msg, 'error'); 
            setTerms([]); 
        } finally { setLoading(false); }
    }, [schoolId, activeFilter]);

    useEffect(() => { fetchTerms(activeFilter); }, [fetchTerms, activeFilter]);

    useEffect(() => {
        const handleStorage = () => { const newId = localStorage.getItem('schoolId') || null; if (newId !== schoolId) setSchoolId(newId); };
        const currentId = localStorage.getItem('schoolId') || null; if (currentId !== schoolId) setSchoolId(currentId);
        window.addEventListener('storage', handleStorage); return () => window.removeEventListener('storage', handleStorage);
    }, [schoolId]);

    // --- Modal & CRUD Logic ---
    const handleOpenCreateModal = () => { if (!schoolId) { showSnackbar("Cannot create: School ID missing.", "warning"); return; } setModalMode('create'); setEditingTerm(null); setModalError(null); setIsModalOpen(true); };
    const handleOpenEditModal = (term) => { setModalMode('edit'); setEditingTerm(term); setModalError(null); setIsModalOpen(true); };
    const handleCloseModal = () => { if (isSaving) return; setIsModalOpen(false); setTimeout(() => { setEditingTerm(null); setModalError(null); }, 300); };

    const handleSaveTerm = async (formData) => {
        setIsSaving(true); setModalError(null); 
        const termId = editingTerm?.id; 
        const url = modalMode === 'edit' && termId ? `${API_URL}/${termId}` : API_URL; 
        const method = modalMode === 'edit' ? 'put' : 'post';
        
        try { 
            const payload = { ...formData }; 
            if (modalMode === 'create' && !payload.schoolId) payload.schoolId = schoolId; 
            await axiosInstance[method](url, payload); 
            showSnackbar(`Fee Term ${modalMode === 'edit' ? 'updated' : 'created'}!`, 'success'); 
            handleCloseModal(); 
            fetchTerms(); 
        } catch (err) { 
            console.error('Save Error:', err); 
            const msg = err.response?.data?.message || `Failed to save.`; 
            setModalError(msg); 
            showSnackbar(msg, 'error'); 
        } finally { setIsSaving(false); }
    };

    const handleDelete = async (termId, termName) => {
        if (!window.confirm(`Delete "${termName || 'ID: ' + termId}"?`)) return; setListError(null);
        try { 
            await axiosInstance.delete(`${API_URL}/${termId}`); 
            showSnackbar(`Fee term deleted.`, 'success'); 
            fetchTerms(); 
        } catch (err) { 
            console.error('Delete Error:', err); 
            const msg = err.response?.data?.message || 'Failed to delete.'; 
            setListError(msg); 
            showSnackbar(msg, 'error'); 
        }
    };

    // --- Render Component Content ---
    const renderContent = () => {
        if (loading) return (
            <CenteredBox>
                <CircularProgress size={40} thickness={4} />
                <Typography sx={{ mt: 2, fontWeight: 500 }} color="text.secondary">Loading Fee Terms...</Typography>
            </CenteredBox>
        );
        
        if (listError && terms.length === 0) return (
            <NoResultsContainer>
                <WarningAmberIcon color="error" sx={{ fontSize: 48, mb: 2, opacity: 0.8 }} />
                <Typography color="error" variant="h6" fontWeight="600" gutterBottom>Loading Failed</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>{listError}</Typography>
                <Button variant="outlined" color="primary" onClick={() => fetchTerms(activeFilter, true)} sx={{ borderRadius: '8px' }}>Retry</Button>
            </NoResultsContainer>
        );
        
        if (!loading && terms.length === 0) {
            const filterText = activeFilter !== 'all' ? ` matching "${activeFilter}" status` : '';
            return (
                <NoResultsContainer>
                    <EventBusyIcon sx={{ fontSize: 50, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.primary" fontWeight="600" gutterBottom>
                        No Fee Terms Found{filterText}
                    </Typography>
                    {schoolId ? (
                        <>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                Add your first term to start scheduling fees.
                            </Typography>
                            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreateModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
                                Create First Term
                            </Button>
                        </>
                    ) : (
                        <Typography color="text.secondary" fontStyle="italic">Select a school to add terms.</Typography>
                    )}
                </NoResultsContainer>
            );
        }

        return (
            <TableContainer sx={{ maxHeight: 650, mt: isMobile ? 0 : 2 }}>
                <StyledTable aria-label="modern fee terms table" stickyHeader>
                    {!isMobile && (
                        <TableHead>
                            <TableRow>
                                <TableCell>Term Name</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell align="center">Late Fee</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Order</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                    )}
                    <TableBody>
                        {terms.map((term) => (
                            <AnimatedTableRow key={term.id}>
                                <StyledTableCell>
                                    <MobileLabel>Term Name:</MobileLabel>
                                    <Typography variant="body2" fontWeight="600">
                                        {term.termName || <Typography component="em" color="text.disabled" fontSize="inherit">(No Name)</Typography>}
                                    </Typography>
                                </StyledTableCell>
                                
                                <StyledTableCell>
                                    <MobileLabel>Due Date:</MobileLabel>
                                    <Typography variant="body2" fontWeight="500">
                                        {formatDateForDisplay(term.dueDate)}
                                    </Typography>
                                </StyledTableCell>
                                
                                <StyledTableCell>
                                     <MobileLabel>Amount:</MobileLabel>
                                     <Typography variant="body2" fontWeight="700" color="text.primary">
                                        {formatAmount(term.percentageDue, term.fixedAmount)}
                                     </Typography>
                                </StyledTableCell>
                                
                                <StyledTableCell align={isMobile ? "right" : "center"}>
                                    <MobileLabel>Late Fee:</MobileLabel>
                                    <Chip
                                        label={term.lateFeeApplicable ? 'Yes' : 'No'} 
                                        color={term.lateFeeApplicable ? 'warning' : 'default'}
                                        size="small"
                                        variant={term.lateFeeApplicable ? 'filled' : 'outlined'}
                                        sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: '50px' }}
                                    />
                                </StyledTableCell>
                                
                                <StyledTableCell align={isMobile ? "right" : "center"}>
                                    <MobileLabel>Status:</MobileLabel>
                                    <Chip
                                        label={term.isActive ? 'Active' : 'Inactive'}
                                        color={term.isActive ? 'success' : 'default'}
                                        size="small"
                                        variant={term.isActive ? 'filled' : 'outlined'}
                                        icon={term.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                                        sx={{ fontWeight: 'bold', borderRadius: '6px', px: 0.5 }}
                                    />
                                </StyledTableCell>
                                
                                <StyledTableCell align={isMobile ? "right" : "center"}>
                                    <MobileLabel>Order:</MobileLabel>
                                    <Typography variant="body2" color="text.secondary">
                                        {term.displayOrder ?? <Typography component="em" color="text.disabled" fontSize="inherit">N/A</Typography>}
                                    </Typography>
                                </StyledTableCell>
                                
                                <StyledTableCell align="right">
                                     <Box display="flex" justifyContent="flex-end" gap={1}> 
                                        <Tooltip title="Edit Term" arrow>
                                            <ActionIconButton className="edit" size="small" onClick={() => handleOpenEditModal(term)} disabled={loading || isModalOpen || isSaving}>
                                                <EditIcon fontSize="small" />
                                            </ActionIconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Term" arrow>
                                            <ActionIconButton className="delete" size="small" onClick={() => handleDelete(term.id, term.termName)} disabled={loading || isModalOpen || isSaving}>
                                                <DeleteIcon fontSize="small" />
                                            </ActionIconButton>
                                        </Tooltip>
                                     </Box>
                                </StyledTableCell>
                            </AnimatedTableRow>
                        ))}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        );
    };

    return (
        <ThemeProvider theme={uiTheme}>
            <PageBackground>
                <Container maxWidth="lg" disableGutters>
                    <StyledCard elevation={0}>
                        <CardContent sx={{ pb: '16px !important', p: { xs: 2, sm: 3 } }}>
                            <HeroStrip>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={7}>
                                        <Typography variant="overline" sx={{ color: 'success.main', fontWeight: 800, letterSpacing: '0.12em' }}>
                                            Term Planner
                                        </Typography>
                                        <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800 }}>
                                            Plan due dates and collection windows in a way admins can read quickly
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                                            Keep term timelines visible, late fee rules easy to scan, and active collection windows obvious for the finance team.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Grid container spacing={1.5}>
                                            <Grid item xs={6}>
                                                <MetricTile>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                                        Terms
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>
                                                        {terms.length}
                                                    </Typography>
                                                </MetricTile>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MetricTile>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}>
                                                        Active
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ mt: 0.7, fontWeight: 800 }}>
                                                        {activeTermsCount}
                                                    </Typography>
                                                </MetricTile>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </HeroStrip>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                                <Typography variant="h6" fontWeight="600" color="text.primary">
                                    Fee Terms Management
                                </Typography>
                                
                                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                    {schoolId && (
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <InputLabel id="status-filter-label">Status Filter</InputLabel>
                                            <Select 
                                                labelId="status-filter-label" 
                                                value={activeFilter} 
                                                label="Status Filter" 
                                                onChange={(e) => setActiveFilter(e.target.value)} 
                                                disabled={loading || isModalOpen}
                                                sx={{ borderRadius: '8px' }}
                                                startAdornment={<FilterListIcon color="action" fontSize="small" sx={{ ml: 1, mr: 0.5 }} />}
                                            >
                                                <MenuItem value="active">Active Terms</MenuItem>
                                                <MenuItem value="inactive">Inactive Terms</MenuItem>
                                                <MenuItem value="all">All Terms</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        startIcon={<AddIcon />} 
                                        onClick={handleOpenCreateModal} 
                                        disabled={!schoolId || loading || isModalOpen}
                                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                                    >
                                        Add Term
                                    </Button>
                                </Box>
                            </Box>
                            
                            {!schoolId && !loading && (
                                <Alert severity="info" sx={{ mt: 2, borderRadius: '8px' }}>
                                    Please select a school to manage fee terms.
                                </Alert>
                            )}

                            <Divider sx={{ my: 2, display: isMobile ? 'none' : 'block' }} />

                            {listError && terms.length > 0 && (
                                <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{listError}</Alert>
                            )}

                            {renderContent()}
                        </CardContent>
                    </StyledCard>

                    {/* SimpleModal remains unchanged in usage, assuming it handles its own internal styling */}
                    <SimpleModal isOpen={isModalOpen} onClose={handleCloseModal} title={modalMode === 'edit' ? 'Edit Fee Term' : 'Create New Fee Term'}>
                        {isModalOpen && ( 
                            <FeeTermForm 
                                key={editingTerm?.id || 'create'} 
                                initialData={editingTerm} 
                                mode={modalMode} 
                                onSave={handleSaveTerm} 
                                onCancel={handleCloseModal} 
                                isLoading={isSaving} 
                                apiError={modalError} 
                                currentSchoolId={schoolId}
                            /> 
                        )}
                    </SimpleModal>

                    <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '8px' }} variant="filled"> 
                            {snackbarMessage} 
                        </Alert>
                    </Snackbar>
                </Container>
            </PageBackground>
        </ThemeProvider>
    );
}

export default FeeTermList;
